import { useState } from "react";
import { useAuth } from "../lib/auth-context";
import { api } from "../lib/api";
import { useApi, useMutation } from "../hooks/useApi";
import { useNav, T, Btn, Input, Card, MetricCard, Spinner, ErrorMsg, Table, StatusBadge, TierBadge } from "../components/shared";
import { LotCard } from "./public";
import { SubscriptionManager } from "./payments";

export function MemberDash() {
  const { user } = useAuth();
  const { setView, setSelectedLot } = useNav();
  const [tab, setTab] = useState("overview");

  const { data: orders, loading: ordersLoading } = useApi(() => api.orders.list(), []);
  const { data: lots,   loading: lotsLoading   } = useApi(() => api.lots.list({ status: "active" }), []);
  const { data: me                              } = useApi(() => api.members.get(user!.id), [user?.id]);
  const { data: subs                            } = useApi(() =>
    fetch("/api/payments/subscriptions", {
      headers: { Authorization: `Bearer ${localStorage.getItem("prov_token")}` }
    }).then(r => r.json()), []
  );

  const activeSub = (subs ?? []).find((s: any) => s.status === "active");
  const hasSubscription = !!activeSub;

  const recentLots = (lots ?? []).slice(0, 3);
  const nextDelivery = (orders ?? []).find((o: any) => o.status === "scheduled" || o.status === "dispatched");

  return (
    <div style={{ paddingTop: 72, minHeight: "100vh", background: T.ivory }}>
      <div style={{ background: T.forest, padding: "52px 72px" }}>
        <span style={{ fontSize: ".52rem", letterSpacing: ".32em", textTransform: "uppercase", color: T.brassLt, display: "block", marginBottom: 12 }}>Member Portal</span>
        <h1 style={{ fontFamily: "'Cormorant',serif", fontSize: "2.5rem", fontWeight: 300, color: T.ivory }}>
          Welcome, <em style={{ fontStyle: "italic", color: T.brassGlow }}>{user?.name?.split(" ")[0]}.</em>
        </h1>
        <div style={{ display: "flex", gap: 4, marginTop: 28 }}>
          {["overview", "orders", "palate", "settings", "subscription"].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ fontFamily: "'Jost',sans-serif", fontSize: ".56rem", letterSpacing: ".2em", textTransform: "uppercase", padding: "10px 22px", background: tab === t ? T.brassLt : "transparent", color: tab === t ? T.forestDeep : "rgba(244,239,228,.55)", border: `1px solid ${tab === t ? T.brassLt : "rgba(152,121,64,.25)"}`, cursor: "pointer", transition: "all .2s" }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Subscribe banner for users without an active subscription */}
      {!hasSubscription && tab === "overview" && (
        <div style={{ background: T.forestDeep, padding: "28px 72px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24 }}>
          <div>
            <p style={{ fontFamily: "'Cinzel',serif", fontSize: ".72rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.brassGlow, marginBottom: 6 }}>No active membership</p>
            <p style={{ fontFamily: "'Libre Baskerville',serif", fontStyle: "italic", fontSize: ".84rem", color: "rgba(244,239,228,.7)" }}>
              Choose a membership plan to start receiving rare single-origin lots with your personal sommelier.
            </p>
          </div>
          <Btn onClick={() => setView("membership")} variant="brass" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
            Choose a Plan →
          </Btn>
        </div>
      )}

      <div style={{ padding: "48px 72px" }} className="fade-in">
        {tab === "overview" && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 2, marginBottom: 40 }}>
              <MetricCard label="Tier" value={hasSubscription ? (user?.tier ?? "—") : "None"} sub={hasSubscription ? "Current membership" : "No active plan"} />
              <MetricCard label="Orders" value={(orders ?? []).length} sub="All time" />
              <MetricCard label="Next delivery"
                value={nextDelivery?.scheduledDate ? new Date(nextDelivery.scheduledDate).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}
                sub={nextDelivery?.lotName ?? "None scheduled"} />
              <MetricCard label="Your Sommelier" value={me?.sommelier?.name?.split(" ")[0] ?? "—"} sub={me?.sommelier?.city ?? "Not yet assigned"} accent />
            </div>

            {hasSubscription ? (
              <>
                <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: ".85rem", letterSpacing: ".15em", color: T.forestDeep, marginBottom: 20 }}>Available to You This Month</h3>
                {lotsLoading ? <Spinner /> : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 2 }}>
                    {recentLots.map((l: any) => <LotCard key={l.id} lot={l} onClick={() => { setSelectedLot(l); setView("lot-detail"); }} />)}
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "60px 0" }}>
                <p style={{ fontFamily: "'Cormorant',serif", fontSize: "1.6rem", fontWeight: 300, color: T.forestDeep, marginBottom: 12 }}>
                  Discover exceptional coffee.
                </p>
                <p style={{ fontFamily: "'Libre Baskerville',serif", fontStyle: "italic", fontSize: ".85rem", color: T.mist, marginBottom: 32, maxWidth: 480, margin: "0 auto 32px" }}>
                  Each month, your personal sommelier selects a rare single-origin lot, curated to your palate, and delivers it with a private tasting ceremony.
                </p>
                <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
                  <Btn onClick={() => setView("membership")} variant="brass">View Membership Plans →</Btn>
                  <Btn onClick={() => setView("origins")} variant="secondary">Browse Coffee Origins</Btn>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "orders" && (
          <div>
            <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: ".85rem", letterSpacing: ".15em", color: T.forestDeep, marginBottom: 24 }}>Order History</h3>
            {ordersLoading ? <Spinner /> : (orders ?? []).length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 0" }}>
                <p style={{ fontFamily: "'Libre Baskerville',serif", fontStyle: "italic", fontSize: ".88rem", color: T.mist }}>No orders yet.</p>
                {!hasSubscription && <Btn onClick={() => setView("membership")} variant="brass" style={{ marginTop: 20 }}>Start a Membership →</Btn>}
              </div>
            ) : (
              <Table
                cols={[
                  { key: "id",            label: "Order",    render: (v: any) => <span style={{ fontFamily: "'Cinzel',serif", fontSize: ".72rem" }}>{v.slice(-8).toUpperCase()}</span> },
                  { key: "lotName",       label: "Lot" },
                  { key: "amountDollars", label: "Amount",   render: (v: any) => `$${v}` },
                  { key: "status",        label: "Status",   render: (v: any) => <StatusBadge status={v} /> },
                  { key: "scheduledDate", label: "Delivery", render: (v: any) => v ? new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—" },
                ]}
                rows={orders ?? []}
              />
            )}
          </div>
        )}

        {tab === "palate"       && <PalateEditor />}
        {tab === "settings"     && <MemberSettings />}
        {tab === "subscription" && <SubscriptionManager />}
      </div>
    </div>
  );
}

function PalateEditor() {
  const { user } = useAuth();
  const [palate, setPalate] = useState({
    palateAcidity:    user?.palateAcidity    ?? 5,
    palateSweetness:  user?.palateSweetness  ?? 5,
    palateBody:       user?.palateBody       ?? 5,
    palateComplexity: user?.palateComplexity ?? 5,
  });
  const [saved, setSaved] = useState(false);
  const { mutate, loading, error } = useMutation(() => api.members.update(user!.id, palate));

  const save = async () => {
    const result = await mutate();
    if (result) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
  };

  const attrs: { key: keyof typeof palate; label: string; desc: string }[] = [
    { key: "palateAcidity",    label: "Acidity",    desc: "Bright & acidic ↔ Smooth & round" },
    { key: "palateSweetness",  label: "Sweetness",  desc: "Savoury & dry ↔ Noticeably sweet" },
    { key: "palateBody",       label: "Body",       desc: "Light & delicate ↔ Full & syrupy" },
    { key: "palateComplexity", label: "Complexity", desc: "Simple & clean ↔ Layered & complex" },
  ];

  return (
    <div style={{ maxWidth: 560 }}>
      <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: ".85rem", letterSpacing: ".15em", color: T.forestDeep, marginBottom: 8 }}>Your Palate Profile</h3>
      <p style={{ fontFamily: "'Libre Baskerville',serif", fontStyle: "italic", fontSize: ".82rem", color: T.mist, marginBottom: 32 }}>Your preferences guide your Sommelier's monthly curation.</p>
      {attrs.map(a => (
        <div key={a.key} style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: ".56rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.smoke }}>{a.label}</span>
            <span style={{ fontFamily: "'Cormorant',serif", fontSize: ".9rem", color: T.brass }}>{palate[a.key]} / 10</span>
          </div>
          <p style={{ fontSize: ".62rem", color: T.mist, marginBottom: 8 }}>{a.desc}</p>
          <input type="range" min={1} max={10} value={palate[a.key]}
            onChange={e => setPalate(p => ({ ...p, [a.key]: parseInt(e.target.value) }))}
            style={{ width: "100%", accentColor: T.forest }} />
        </div>
      ))}
      {error && <ErrorMsg msg={error} />}
      <Btn onClick={save} disabled={loading} style={{ marginTop: 8 }}>
        {saved ? "✓ Saved" : loading ? "Saving…" : "Save Preferences"}
      </Btn>
    </div>
  );
}

function MemberSettings() {
  const { user } = useAuth();
  const [city, setCity] = useState(user?.city ?? "");
  const [name, setName] = useState(user?.name ?? "");
  const [saved, setSaved] = useState(false);
  const { mutate, loading, error } = useMutation(() => api.members.update(user!.id, { city, name }));

  const save = async () => {
    const result = await mutate();
    if (result) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
  };

  return (
    <div style={{ maxWidth: 480 }}>
      <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: ".85rem", letterSpacing: ".15em", color: T.forestDeep, marginBottom: 24 }}>Account Settings</h3>
      <Card>
        <Input label="Full Name" value={name} onChange={setName} />
        <div style={{ marginBottom: 20 }}>
          <p style={{ fontSize: ".56rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.mist, marginBottom: 4 }}>Email</p>
          <p style={{ fontSize: ".82rem", color: T.smoke }}>{user?.email}</p>
        </div>
        <div style={{ marginBottom: 20 }}>
          <p style={{ fontSize: ".56rem", letterSpacing: ".2em", textTransform: "uppercase", color: T.mist, marginBottom: 4 }}>Membership Tier</p>
          <TierBadge tier={user?.tier} />
        </div>
        <Input label="City" value={city} onChange={setCity} placeholder="Your city" />
        {error && <ErrorMsg msg={error} />}
        <Btn onClick={save} disabled={loading} small>
          {saved ? "✓ Saved" : loading ? "Saving…" : "Save Changes"}
        </Btn>
      </Card>
    </div>
  );
}
